import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    FastForward,
    FastRewind,
    Fullscreen,
    FullscreenExit,
    Pause,
    PlayArrow,
    VolumeOff,
    VolumeUp,
} from '@mui/icons-material'

const formatTime = (value) => {
    const seconds = Math.max(0, Number(value || 0))
    if (!Number.isFinite(seconds)) return '0:00'

    const whole = Math.floor(seconds)
    const hours = Math.floor(whole / 3600)
    const minutes = Math.floor((whole % 3600) / 60)
    const secs = whole % 60

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    }

    return `${minutes}:${String(secs).padStart(2, '0')}`
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

export default function SiteVideoPlayer({ src, title = 'Видео' }) {
    const rootRef = useRef(null)
    const videoRef = useRef(null)
    const hideTimerRef = useRef(null)

    const [playing, setPlaying] = useState(false)
    const [duration, setDuration] = useState(0)
    const [currentTime, setCurrentTime] = useState(0)
    const [bufferedEnd, setBufferedEnd] = useState(0)
    const [volume, setVolume] = useState(1)
    const [muted, setMuted] = useState(false)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [fullscreen, setFullscreen] = useState(false)
    const [controlsVisible, setControlsVisible] = useState(true)
    const [error, setError] = useState('')

    const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0
    const buffered = duration > 0 ? Math.min(100, Math.max(0, (bufferedEnd / duration) * 100)) : 0

    const syncBuffered = useCallback(() => {
        const video = videoRef.current
        if (!video || !video.buffered?.length) {
            setBufferedEnd(0)
            return
        }

        try {
            setBufferedEnd(video.buffered.end(video.buffered.length - 1))
        } catch (_error) {
            setBufferedEnd(0)
        }
    }, [])

    const togglePlayback = useCallback(() => {
        const video = videoRef.current
        if (!video) return

        if (video.paused || video.ended) {
            video.play().catch(() => setError('Браузер не смог запустить это видео'))
        } else {
            video.pause()
        }
    }, [])

    const seekBy = useCallback((delta) => {
        const video = videoRef.current
        if (!video) return
        const next = Math.min(Number(video.duration || 0), Math.max(0, Number(video.currentTime || 0) + delta))
        video.currentTime = next
        setCurrentTime(next)
    }, [])

    const seekTo = useCallback((value) => {
        const video = videoRef.current
        if (!video || !duration) return
        const next = Math.min(duration, Math.max(0, Number(value || 0)))
        video.currentTime = next
        setCurrentTime(next)
    }, [duration])

    const setVideoVolume = useCallback((value) => {
        const video = videoRef.current
        const next = Math.min(1, Math.max(0, Number(value || 0)))
        if (!video) return
        video.volume = next
        video.muted = next === 0 ? true : muted
        setVolume(next)
        if (next > 0 && muted) {
            video.muted = false
            setMuted(false)
        }
    }, [muted])

    const toggleMute = useCallback(() => {
        const video = videoRef.current
        if (!video) return
        const next = !video.muted
        video.muted = next
        setMuted(next)
    }, [])

    const setSpeed = useCallback((value) => {
        const video = videoRef.current
        const next = SPEEDS.includes(Number(value)) ? Number(value) : 1
        if (video) video.playbackRate = next
        setPlaybackRate(next)
    }, [])

    const toggleFullscreen = useCallback(async () => {
        const root = rootRef.current
        if (!root) return

        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen?.()
            } else {
                await root.requestFullscreen?.()
            }
        } catch (_error) {
            // Browser may reject fullscreen without a direct user gesture.
        }
    }, [])

    const showControls = useCallback(() => {
        setControlsVisible(true)
        if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)

        if (playing) {
            hideTimerRef.current = window.setTimeout(() => {
                setControlsVisible(false)
            }, 2200)
        }
    }, [playing])

    useEffect(() => {
        showControls()
        return () => {
            if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current)
        }
    }, [playing, showControls])

    useEffect(() => {
        const onFullscreenChange = () => setFullscreen(!!document.fullscreenElement)
        document.addEventListener('fullscreenchange', onFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
    }, [])

    useEffect(() => {
        const onKeyDown = (event) => {
            if (!rootRef.current || !document.body.contains(rootRef.current)) return
            if (event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) return

            const key = String(event.key || '').toLowerCase()

            if (key === ' ' || key === 'k') {
                event.preventDefault()
                event.stopImmediatePropagation?.()
                event.stopPropagation()
                togglePlayback()
                return
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault()
                event.stopImmediatePropagation?.()
                event.stopPropagation()
                seekBy(-10)
                return
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault()
                event.stopImmediatePropagation?.()
                event.stopPropagation()
                seekBy(10)
                return
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault()
                event.stopImmediatePropagation?.()
                event.stopPropagation()
                setVideoVolume(Math.min(1, volume + 0.05))
                return
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault()
                event.stopImmediatePropagation?.()
                event.stopPropagation()
                setVideoVolume(Math.max(0, volume - 0.05))
                return
            }
            if (key === 'm') {
                event.preventDefault()
                event.stopImmediatePropagation?.()
                event.stopPropagation()
                toggleMute()
                return
            }
            if (key === 'f') {
                event.preventDefault()
                event.stopImmediatePropagation?.()
                event.stopPropagation()
                toggleFullscreen()
            }
        }

        window.addEventListener('keydown', onKeyDown, true)
        return () => window.removeEventListener('keydown', onKeyDown, true)
    }, [seekBy, setVideoVolume, toggleFullscreen, toggleMute, togglePlayback, volume])

    useEffect(() => {
        setPlaying(false)
        setDuration(0)
        setCurrentTime(0)
        setBufferedEnd(0)
        setError('')
    }, [src])

    const rateLabel = useMemo(() => `${playbackRate}×`, [playbackRate])

    return (
        <div
            ref={rootRef}
            className={`site-video-player${controlsVisible || !playing ? ' is-controls-visible' : ''}${fullscreen ? ' is-fullscreen' : ''}`}
            onMouseMove={showControls}
            onMouseLeave={() => playing && setControlsVisible(false)}
            onTouchStart={showControls}
            onDoubleClick={toggleFullscreen}
        >
            <video
                ref={videoRef}
                className="site-video-player__video"
                src={src}
                preload="metadata"
                playsInline
                onClick={togglePlayback}
                onPlay={() => { setPlaying(true); setError('') }}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onLoadedMetadata={(event) => {
                    const nextDuration = Number(event.currentTarget.duration || 0)
                    setDuration(Number.isFinite(nextDuration) ? nextDuration : 0)
                    setVolume(Number(event.currentTarget.volume || 1))
                    setMuted(!!event.currentTarget.muted)
                    setPlaybackRate(Number(event.currentTarget.playbackRate || 1))
                }}
                onDurationChange={(event) => {
                    const nextDuration = Number(event.currentTarget.duration || 0)
                    setDuration(Number.isFinite(nextDuration) ? nextDuration : 0)
                }}
                onTimeUpdate={(event) => {
                    setCurrentTime(Number(event.currentTarget.currentTime || 0))
                    syncBuffered()
                }}
                onProgress={syncBuffered}
                onVolumeChange={(event) => {
                    setVolume(Number(event.currentTarget.volume || 0))
                    setMuted(!!event.currentTarget.muted)
                }}
                onRateChange={(event) => setPlaybackRate(Number(event.currentTarget.playbackRate || 1))}
                onError={() => setError('Не удалось воспроизвести видео. Формат или кодек может не поддерживаться браузером.')}
                aria-label={title}
            />

            {!playing ? (
                <button type="button" className="site-video-player__center-play" onClick={togglePlayback} aria-label="Запустить видео">
                    <PlayArrow />
                </button>
            ) : null}

            {error ? <div className="site-video-player__error">{error}</div> : null}

            <div className="site-video-player__controls" onClick={(event) => { event.stopImmediatePropagation?.(); event.stopPropagation(); }}>
                <div className="site-video-player__timeline-wrap">
                    <div className="site-video-player__timeline-buffered" style={{ width: `${buffered}%` }} />
                    <div className="site-video-player__timeline-progress" style={{ width: `${progress}%` }} />
                    <input
                        className="site-video-player__timeline"
                        type="range"
                        min="0"
                        max={duration || 0}
                        step="0.01"
                        value={Math.min(currentTime, duration || 0)}
                        onChange={(event) => seekTo(event.target.value)}
                        aria-label="Позиция видео"
                    />
                </div>

                <div className="site-video-player__control-row">
                    <button type="button" onClick={togglePlayback} title={playing ? 'Пауза (Space)' : 'Воспроизвести (Space)'}>
                        {playing ? <Pause /> : <PlayArrow />}
                    </button>
                    <button type="button" onClick={() => seekBy(-10)} title="Назад на 10 секунд">
                        <FastRewind />
                        <small>10</small>
                    </button>
                    <button type="button" onClick={() => seekBy(10)} title="Вперёд на 10 секунд">
                        <FastForward />
                        <small>10</small>
                    </button>

                    <span className="site-video-player__time">{formatTime(currentTime)} / {formatTime(duration)}</span>

                    <div className="site-video-player__spacer" />

                    <button type="button" onClick={toggleMute} title={muted ? 'Включить звук' : 'Выключить звук'}>
                        {muted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
                    </button>
                    <input
                        className="site-video-player__volume"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={muted ? 0 : volume}
                        onChange={(event) => setVideoVolume(event.target.value)}
                        aria-label="Громкость"
                    />

                    <label className="site-video-player__speed" title="Скорость воспроизведения">
                        <span>{rateLabel}</span>
                        <select value={playbackRate} onChange={(event) => setSpeed(event.target.value)}>
                            {SPEEDS.map((speed) => <option key={speed} value={speed}>{speed}×</option>)}
                        </select>
                    </label>

                    <button type="button" onClick={toggleFullscreen} title="На весь экран (F)">
                        {fullscreen ? <FullscreenExit /> : <Fullscreen />}
                    </button>
                </div>
            </div>
        </div>
    )
}
